import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { handleApiError } from './axiosConfig';
import { Camp, CampDate } from '../utils/types';

export interface ScheduledCamp {
  camp: Camp
  camp_date: CampDate
}

export interface CustomEvent {
  id: string | number;
  title: string;
  start_date: string;
  end_date: string;
}

export interface AddCampToScheduleRequest {
  scheduleUuid: string;
  campId: string;
  campDateId: string;
}

export interface RemoveCampFromScheduleRequest {
  scheduleUuid: string;
  campId: string;
  campDateId: string;
}

export interface ShareScheduleResponse {
  share_uuid: string;
}

export interface CreateCustomEventRequest {
  scheduleUuid: string;
  title: string;
  startDate: string;
  endDate: string;
}

export interface UpdateCustomEventRequest {
  scheduleUuid: string;
  eventId: string | number;
  title: string;
  startDate: string;
  endDate: string;
}

export interface DeleteCustomEventRequest {
  scheduleUuid: string;
  eventId: string | number;
}

export interface CreateScheduleRequest {
  name: string;
}

export interface CreateScheduleResponse {
  uuid: string;
  name: string;
}

export interface ScheduleItem {
  uuid: string;
  name: string;
  participant_id: number;
  scheduled_camps: ScheduledCamp[];
  custom_events?: CustomEvent[];
  share_uuid?: string;
  created_at: string;
}

export interface SchedulesResponse {
  my_calendars: ScheduleItem[];
  shared_calendars: ScheduleItem[];
}

// GET /api/users/me/schedules
export const useGetSchedules = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['schedules'],
    queryFn: async (): Promise<SchedulesResponse> => {
      try {
        const response = await apiClient.get<SchedulesResponse>('/api/users/me/schedules');
        return response;
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch schedules:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: options?.enabled !== false,
  });
};

// GET /api/schedules/{scheduleUuid}
export const useGetSchedule = (scheduleUuid: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['schedule', scheduleUuid],
    queryFn: async (): Promise<ScheduleItem> => {
      try {
        return await apiClient.get<ScheduleItem>(`/api/users/me/schedules/${scheduleUuid}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: options?.enabled !== false && Boolean(scheduleUuid),
  });
};

// POST /api/schedules/{scheduleUuid}/camps { camp_id }
export const useAddCampToSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid: scheduleUuid, campId, campDateId }: AddCampToScheduleRequest): Promise<ScheduleItem> => {
      try {
        const body: Record<string, any> = { camp_id: campId, camp_date_id: campDateId };
        return await apiClient.post<ScheduleItem>(`/api/users/me/schedules/${scheduleUuid}/camps`, body);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to add camp to schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, variables) => {
      // refresh the schedule cache
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.scheduleUuid] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.setQueryData(['schedule', variables.scheduleUuid], data);
    },
    onError: (error) => {
      console.error('Failed to add camp to schedule:', error);
    },
  });
};

// DELETE /api/schedules/{scheduleUuid}/camps/{campId}?camp_date_id={campDateId}
export const useRemoveCampFromSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid: scheduleUuid, campId, campDateId }: RemoveCampFromScheduleRequest): Promise<ScheduleItem> => {
      try {
        const endpoint = `/api/users/me/schedules/${scheduleUuid}/camps/${campId}/dates/${campDateId}`;
        return await apiClient.delete<ScheduleItem>(endpoint);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to remove camp from schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.scheduleUuid] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.setQueryData(['schedule', variables.scheduleUuid], data);
    },
    onError: (error) => {
      console.error('Failed to remove camp from schedule:', error);
    },
  });
};

// POST /api/users/me/schedules/{scheduleUuid}/share
export const useShareSchedule = () => {
  return useMutation({
    mutationFn: async (scheduleUuid: string): Promise<ShareScheduleResponse> => {
      try {
        return await apiClient.post<ShareScheduleResponse>(`/api/users/me/schedules/${scheduleUuid}/share`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to generate schedule share link:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Failed to generate schedule share link:', error);
    },
  });
};

// POST /api/users/me/schedules/{scheduleUuid}/events
export const useCreateCustomEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, title, startDate, endDate }: CreateCustomEventRequest): Promise<CustomEvent> => {
      try {
        const body = { title, start_date: startDate, end_date: endDate };
        return await apiClient.post<CustomEvent>(`/api/users/me/schedules/${scheduleUuid}/events`, body);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create custom event:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.scheduleUuid] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to create custom event:', error);
    },
  });
};

// PUT /api/users/me/schedules/{scheduleUuid}/events/{eventId}
export const useUpdateCustomEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, eventId, title, startDate, endDate }: UpdateCustomEventRequest): Promise<CustomEvent> => {
      try {
        const body = { title, start_date: startDate, end_date: endDate };
        return await apiClient.put<CustomEvent>(`/api/users/me/schedules/${scheduleUuid}/events/${eventId}`, body);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update custom event:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.scheduleUuid] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to update custom event:', error);
    },
  });
};

// DELETE /api/users/me/schedules/{scheduleUuid}/events/{eventId}
export const useDeleteCustomEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, eventId }: DeleteCustomEventRequest): Promise<CustomEvent> => {
      try {
        return await apiClient.delete<CustomEvent>(`/api/users/me/schedules/${scheduleUuid}/events/${eventId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete custom event:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule', variables.scheduleUuid] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to delete custom event:', error);
    },
  });
};

// POST /api/users/me/schedules
export const useCreateSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name }: CreateScheduleRequest): Promise<CreateScheduleResponse> => {
      try {
        const body = { name };
        return await apiClient.post<CreateScheduleResponse>('/api/users/me/schedules', body);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      // Invalidate any schedules list queries if they exist
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to create schedule:', error);
    },
  });
};

// PATCH /api/users/me/schedules/{scheduleUuid}
export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, name }: { scheduleUuid: string; name: string }): Promise<ScheduleItem> => {
      try {
        const body = { name };
        return await apiClient.put<ScheduleItem>(`/api/users/me/schedules/${scheduleUuid}`, body);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to update schedule:', error);
    },
  });
};

// DELETE /api/users/me/schedules/{scheduleUuid}
export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleUuid: string): Promise<void> => {
      try {
        await apiClient.delete<void>(`/api/users/me/schedules/${scheduleUuid}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to delete schedule:', error);
    },
  });
};

// DELETE /api/users/me/schedules/{scheduleUuid}/share/{shareUuid}
export const useDeleteSharedSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, shareUuid }: { scheduleUuid: string; shareUuid: string }): Promise<void> => {
      try {
        await apiClient.delete<void>(`/api/users/me/schedules/${scheduleUuid}/share/${shareUuid}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete shared schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to delete shared schedule:', error);
    },
  });
};

export const useGetSharedSchedule = (scheduleUuid: string, shareId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['sharedSchedule', scheduleUuid, shareId],
    queryFn: async (): Promise<ScheduleItem> => {
      try {
        return await apiClient.get<ScheduleItem>(`/api/users/me/schedules/${scheduleUuid}/share/${shareId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch shared schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: options?.enabled !== false && Boolean(scheduleUuid) && Boolean(shareId),
  });
};

// POST /api/users/me/schedules/{scheduleUuid}/save?share_id={shareId}
export const useSaveSharedSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleUuid, shareId }: { scheduleUuid: string; shareId: string }): Promise<ScheduleItem> => {
      try {
        return await apiClient.post<ScheduleItem>(`/api/users/me/schedules/${scheduleUuid}/share/${shareId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to save shared schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error) => {
      console.error('Failed to save shared schedule:', error);
    },
  });
};
